import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  ButtonGroup,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  format,
  addDays,
  startOfWeek,
  isSameDay,
  parseISO,
  differenceInCalendarDays,
} from "date-fns";
import { motion } from "framer-motion";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const rooms = [
  {
    id: 202,
    bookings: [
      { name: "Rohan Sharma", start: "2025-12-17", end: "2025-12-20", status: "booked" },
    ],
  },
  {
    id: 203,
    bookings: [
      { name: "Anjali Singh", start: "2025-12-18", end: "2025-12-21", status: "booked" },
    ],
  },
  {
    id: 204,
    bookings: [
      { name: "Suresh Patel", start: "2025-12-22", end: "2025-12-24", status: "upcoming" },
    ],
  },
  {
    id: 205,
    bookings: [
      { name: "Nina Rao", start: "2025-12-19", end: "2025-12-20", status: "cancelled" },
    ],
  },
  {
    id: 206,
    bookings: [],
  },
];

const getStatusColor = (status) => {
  switch (status) {
    case "booked":
      return "#A5D6A7"; // green
    case "upcoming":
      return "#90CAF9"; // blue
    case "cancelled":
      return "#EF9A9A"; // red
    default:
      return "#E0E0E0"; // grey
  }
};

export default function MotelCalendarBoard() {
  const [selectedDate, setSelectedDate] = useState(new Date("2025-12-18"));
  const [viewMode, setViewMode] = useState("week");

  const handlePrev = () => {
    setSelectedDate((d) =>
      viewMode === "day" ? addDays(d, -1) : addDays(d, -7)
    );
  };
  const handleNext = () => {
    setSelectedDate((d) =>
      viewMode === "day" ? addDays(d, 1) : addDays(d, 7)
    );
  };

  const days = useMemo(() => {
    if (viewMode === "day") return [selectedDate];
    const start = startOfWeek(selectedDate, { weekStartsOn: 0 });
    return Array.from({ length: 7 }).map((_, i) => addDays(start, i));
  }, [selectedDate, viewMode]);

  const totalRooms = rooms.length;
  const occupiedCount = rooms.filter((r) =>
    r.bookings.some((b) => {
      const start = parseISO(b.start);
      const end = parseISO(b.end);
      return selectedDate >= start && selectedDate <= end;
    })
  ).length;
  const occupancyPercent = Math.round((occupiedCount / totalRooms) * 100);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper sx={{ minHeight: "100vh", bgcolor: "#fafafa" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid #ddd",
            bgcolor: "white",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#1976D2" }}>
              Tidy Inn Calendar
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Total Rooms: {totalRooms} |{" "}
              <span style={{ color: "#388E3C" }}>{occupancyPercent}% Occupied</span>
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              onClick={() => setSelectedDate(new Date())}
              variant="outlined"
              size="small"
            >
              Today
            </Button>
            <IconButton size="small" onClick={handlePrev}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton size="small" onClick={handleNext}>
              <ChevronRightIcon />
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {format(selectedDate, "MMMM yyyy")}
            </Typography>
            <ButtonGroup variant="outlined" size="small">
              <Button
                onClick={() => setViewMode("day")}
                variant={viewMode === "day" ? "contained" : "outlined"}
              >
                Day
              </Button>
              <Button
                onClick={() => setViewMode("week")}
                variant={viewMode === "week" ? "contained" : "outlined"}
              >
                Week
              </Button>
            </ButtonGroup>
          </Box>
        </Box>

        {/* Date Header */}
        <Box sx={{ display: "flex", borderBottom: "1px solid #ddd", bgcolor: "#f5f5f5" }}>
          <Box sx={{ width: 100, bgcolor: "#f5f5f5" }} />
          {days.map((day) => (
            <Box
              key={day}
              sx={{
                flex: 1,
                textAlign: "center",
                borderRight: "1px solid #e0e0e0",
                py: 1,
                bgcolor: isSameDay(day, new Date()) ? "#E3F2FD" : "#f5f5f5",
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {format(day, "EEE")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {format(day, "MMM d")}
              </Typography>
              <Typography variant="caption" sx={{ mt: 0.5, color: "#388E3C" }}>
                {Math.floor(Math.random() * 10) + 1} Available
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Rooms */}
        <Box>
          {rooms.map((room) => (
            <Box
              key={room.id}
              sx={{
                display: "flex",
                borderBottom: "1px solid #e0e0e0",
                position: "relative",
                height: 56,
                bgcolor: "#fff",
              }}
            >
              {/* Room Number */}
              <Box
                sx={{
                  width: 100,
                  textAlign: "center",
                  borderRight: "1px solid #ddd",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  fontWeight: 600,
                  bgcolor: "#FAFAFA",
                }}
              >
                <Typography variant="body2">{room.id}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {room.bookings.length ? "Occupied" : "Available"}
                </Typography>
              </Box>

              {/* Empty cells */}
              {days.map((_, i) => (
                <Box key={i} sx={{ flex: 1, borderRight: "1px solid #eee" }} />
              ))}

              {/* Booking Bars */}
              {room.bookings.map((b, idx) => {
                const start = parseISO(b.start);
                const end = parseISO(b.end);
                if (end < days[0] || start > days[days.length - 1]) return null;

                const visibleStart = Math.max(0, (Math.max(start, days[0]) - days[0]) / (1000 * 60 * 60 * 24));
                const visibleEnd = Math.min(days.length, (Math.min(end, days[days.length - 1]) - days[0]) / (1000 * 60 * 60 * 24) + 1);
                const barWidth = (visibleEnd - visibleStart) * (100 / days.length) + "%";

                return (
                  <Tooltip key={idx} title={`${b.name} (${b.status})`}>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.4 }}
                      style={{
                        position: "absolute",
                        left: `calc(${visibleStart * (100 / days.length)}% + 100px)`,
                        top: 8,
                        height: 40,
                        width: barWidth,
                        background: getStatusColor(b.status),
                        borderRadius: 6,
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: "#2E2E2E",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      }}
                    >
                      {b.name}
                    </motion.div>
                  </Tooltip>
                );
              })}
            </Box>
          ))}
        </Box>

        {/* Footer Legend */}
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "center", gap: 3, p: 1.5, bgcolor: "white" }}>
          <Legend color="#A5D6A7" label="Booked" />
          <Legend color="#90CAF9" label="Upcoming" />
          <Legend color="#EF9A9A" label="Cancelled" />
        </Box>
      </Paper>
    </LocalizationProvider>
  );
}

const Legend = ({ color, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Box sx={{ width: 14, height: 14, bgcolor: color, borderRadius: "4px" }} />
    <Typography variant="caption">{label}</Typography>
  </Box>
);
